import React, { useEffect } from 'react';
import type { FC } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import EmptyState from '@atlaskit/empty-state';
import { Box, xcss } from '@atlaskit/primitives';

import { getMonitoringClient } from '@confluence/monitoring';
import { Attribution } from '@confluence/error-boundary';

import noDataImage from '../assets/Warning.svg';

type CustomSitesExtensionNoDataComponentProps = {
	source: string;
	isConfigPanel?: boolean;
};

const baseStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

export const i18n = defineMessages({
	noDataStateHeaderText: {
		id: 'custom-sites-extensions.link-cards.error-states.no-data.header-text',
		defaultMessage: 'No data to display',
		description:
			'Header text shown when a cards extension or config panel cannot be rendered because data is missing',
	},
	noDataStateDescription: {
		id: 'custom-sites-extensions.link-cards.error-states.no-data.description-text',
		defaultMessage: 'Try deleting this macro and creating a new one.',
		description:
			'Describes possible action when cards extension or config panel is unable to render because of no data',
	},
});

export const CustomSitesExtensionNoDataComponent: FC<CustomSitesExtensionNoDataComponentProps> = ({
	source,
	isConfigPanel,
}) => {
	const intl = useIntl();

	const extensionSourceName = source?.charAt(0).toUpperCase() + source?.slice(1);

	useEffect(() => {
		const errorMessage = `${extensionSourceName} ${
			isConfigPanel ? 'config panel' : 'extension'
		} failed to render. No parameters.`;

		getMonitoringClient().submitError(new Error(errorMessage), {
			attribution: Attribution.COMPANY_HUB,
		});
	}, [extensionSourceName, isConfigPanel]);

	return (
		<Box
			xcss={[
				baseStyles,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
				isConfigPanel ? xcss({ height: '90%' }) : xcss({ height: '100%' }),
			]}
		>
			<EmptyState
				header={intl.formatMessage(i18n.noDataStateHeaderText)}
				description={<FormattedMessage {...i18n.noDataStateDescription} />}
				imageUrl={noDataImage}
			/>
		</Box>
	);
};
